import { Ppp, PppFilter, IUpsertPpp, PppReorderItem, PaginatedResponse, EntityResponse } from "@/types";
import { apiClient } from "@/lib/apiClient";

/** BE may wrap single resources as `{ data: entity }`. */
function unwrap<T>(raw: T | EntityResponse<T>): T {
  return raw && typeof raw === "object" && "data" in raw ? (raw as EntityResponse<T>).data : (raw as T);
}

function parsePpp(raw: Ppp | EntityResponse<Ppp>): Ppp {
  const p = unwrap(raw);
  return {
    ...p,
    keyResults: Array.isArray(p.keyResults) ? p.keyResults : [],
    deliverableCount: typeof p.deliverableCount === "number" ? p.deliverableCount : 0,
    isConfidential: p.isConfidential ?? false,
    members: p.members ?? [],
  };
}

export const pppService = {
  getPpps: async (params?: PppFilter): Promise<PaginatedResponse<Ppp>> => {
    const queryParams: Record<string, string> = {
      page: (params?.page || 1).toString(),
      limit: (params?.limit || 10).toString(),
    };
    if (params?.type) queryParams.type = params.type;
    if (params?.category) queryParams.category = params.category;
    if (params?.keyResultPid) queryParams.keyResultPid = params.keyResultPid;
    if (params?.search) queryParams.search = params.search;
    if (params?.sortBy) queryParams.sortBy = params.sortBy;
    if (params?.sortOrder) queryParams.sortOrder = params.sortOrder;

    const res = await apiClient.get<{ data?: Ppp[]; total?: number }>("/api/ppps", { params: queryParams });
    const rows = res.data ?? [];
    return { data: rows.map(parsePpp), total: typeof res.total === "number" ? res.total : rows.length };
  },

  getPpp: async (pid: string): Promise<Ppp> =>
    parsePpp(await apiClient.get<Ppp | EntityResponse<Ppp>>(`/api/ppps/${pid}`)),

  createPpp: async (data: IUpsertPpp): Promise<Ppp> =>
    parsePpp(await apiClient.post<Ppp | EntityResponse<Ppp>>("/api/ppps", data)),

  updatePpp: async (pid: string, data: Partial<IUpsertPpp>): Promise<Ppp> =>
    parsePpp(await apiClient.put<Ppp | EntityResponse<Ppp>>(`/api/ppps/${pid}`, data)),

  deletePpp: async (pid: string): Promise<void> => apiClient.delete<void>(`/api/ppps/${pid}`),

  reorderPpps: async (items: PppReorderItem[]): Promise<void> =>
    apiClient.post<void>("/api/ppps/reorder", { items }),
};
